import type { UploadItem, UrlPreview } from "../types";
import { uploadThumbUrl } from "../api";
import { IconCheck, IconPlus } from "./icons";
import { Spinner, cx } from "./ui";

/**
 * プロンプトに URL が含まれているときに、読み込み結果を見せるパネル。
 *
 * 本文は自動でモデルに渡すが、画像は押したときだけ参照画像に入れる。
 * 参照画像を付けると生成が i2i（元画像の加工）に変わってしまい、
 * 「URLを参考にした新しい絵」を期待している場合と結果が食い違うため。
 */
export function UrlSources({
  sources,
  loading,
  images,
  attachedIds,
  onToggleImage,
}: {
  sources: UrlPreview[];
  loading: boolean;
  images: UploadItem[];
  attachedIds: string[];
  onToggleImage: (image: UploadItem) => void;
}) {
  if (!loading && sources.length === 0) return null;

  const failed = sources.filter((s) => !s.ok);

  return (
    <div
      className={cx(
        "mt-2 rounded-xl border p-2.5",
        failed.length > 0 && !loading ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50",
      )}
    >
      {failed.length > 0 && !loading && (
        <p className="mb-1.5 text-[12.5px] font-bold leading-relaxed text-rose-700">
          このURLは読み込めませんでした。この状態で生成しても、URLの内容は画像に反映されません。
        </p>
      )}
      <div className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-bold text-slate-500">
        {loading ? <Spinner className="h-3.5 w-3.5" /> : <span aria-hidden>🔗</span>}
        {loading ? "URLを読み込み中…" : "URLの本文を参考にします"}
      </div>

      <ul className="space-y-1.5">
        {sources.map((s) => (
          <li key={s.url} className="flex items-start gap-2">
            <span
              className={cx(
                "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold",
                s.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700",
              )}
            >
              {s.ok ? `${s.chars}字` : "失敗"}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-semibold text-slate-700">{s.title || s.url}</p>
              <p className="truncate text-[11.5px] text-slate-400">{s.ok ? s.excerpt : s.error}</p>
              {s.ok && s.rendered && (
                <p className="text-[11.5px] text-emerald-600">
                  JavaScriptで描画されるページのため、ブラウザで開いて読み取りました
                </p>
              )}
              {s.ok && s.thin && (
                <p className="text-[11.5px] font-semibold text-amber-600">
                  本文が{s.chars}字しか取れていません。JavaScriptで描画されるページの可能性があります
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {images.length > 0 && (
        <div className="mt-2.5 border-t border-slate-200 pt-2">
          <p className="mb-1.5 text-[12px] text-slate-500">
            ページ内の画像です。
            <span className="font-semibold">押すと参照画像</span>
            になります（元画像を加工する生成に変わります）
          </p>
          <div className="flex flex-wrap gap-2">
            {images.map((img) => {
              const on = attachedIds.includes(img.id);
              return (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => onToggleImage(img)}
                  title={on ? "参照画像から外す" : "参照画像に使う"}
                  className={cx(
                    "relative h-16 w-16 overflow-hidden rounded-lg border-2 transition",
                    on ? "border-brand-500" : "border-slate-200 hover:border-brand-300",
                  )}
                >
                  <img src={uploadThumbUrl(img.url, 192)} alt={img.name} loading="lazy" className="h-full w-full object-cover" />
                  <span
                    className={cx(
                      "absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full text-white",
                      on ? "bg-brand-500" : "bg-slate-900/50",
                    )}
                  >
                    {on ? <IconCheck className="h-2.5 w-2.5" /> : <IconPlus className="h-2.5 w-2.5" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
